import { useState } from "react";
import { AlertTriangle, ArrowRight, Check, Plus, GitBranch, Search, X } from "lucide-react";

import type { Account, AccountRoleAssignment, AccountRoleListing } from "../../services/financialService";
import AccountSelect from "./AccountSelect";
import AIAssistField from "../AIAssistField";
import { roleStage, roleStageCounts, TYPE_DOTS, type AccountType, type RoleStage } from "./chartOfAccounts";

interface Props {
  listing?: AccountRoleListing;
  accounts?: Account[];
  canPropose: boolean;
  canReview: boolean;
  canApprove: boolean;
  onPropose: (data: { role: string; account: number }) => void;
  onSubmit: (role: string) => void;
  onSubmitAll: () => void;
  onVerifyAll: () => void;
  onApproveAll: () => void;
  onVerify: (data: { role: string; comment?: string }) => void;
  onReturn: (data: { role: string; reason: string }) => void;
  onApprove: (role: string) => void;
  /** Opens the new-account form for a type no approved account covers yet. */
  onCreateAccount?: (type: AccountType) => void;
}

const STATUS: Record<string, { label: string; dot: string }> = {
  unassigned: { label: "Not set up", dot: "bg-amber-500" },
  draft: { label: "Not sent for review", dot: "bg-gray-400" },
  submitted: { label: "Waiting for review", dot: "bg-blue-500" },
  verified: { label: "Reviewed, waiting for approval", dot: "bg-indigo-500" },
  approved: { label: "In use", dot: "bg-green-500" },
  returned: { label: "Sent back for changes", dot: "bg-orange-500" },
  rejected: { label: "Rejected", dot: "bg-red-500" },
};

const STAGE_TABS: ["" | RoleStage, string][] = [
  ["", "All"],
  ["unassigned", "Not set up"],
  ["in_progress", "In progress"],
  ["in_use", "In use"],
];

const smallButton =
  "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-50";

export default function AccountRolesPanel({
  listing, accounts, canPropose, canReview, canApprove,
  onPropose, onSubmit, onSubmitAll, onVerifyAll, onApproveAll, onVerify, onReturn, onApprove, onCreateAccount,
}: Props) {
  const [returning, setReturning] = useState<{ role: string; label: string } | null>(null);
  const [reason, setReason] = useState("");
  const [stage, setStage] = useState<"" | RoleStage>("");
  const [search, setSearch] = useState("");

  if (!listing) {
    return (
      <div className="card card-content space-y-2 pt-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="h-10 animate-pulse rounded bg-gray-100" />
        ))}
      </div>
    );
  }

  // What each bulk action would act on. Review and approval cover first-time
  // setup only: a role that already has an account is live, and re-pointing it
  // redirects money as soon as it is approved, so that is done one at a time.
  const readyToSend = listing.roles.filter(
    (role) => role.pending_account && ["draft", "returned"].includes(role.workflow_status),
  ).length;
  const readyToReview = listing.roles.filter(
    (role) => role.pending_account && !role.account && role.workflow_status === "submitted",
  ).length;
  const readyToApprove = listing.roles.filter(
    (role) => role.pending_account && !role.account && role.workflow_status === "verified",
  ).length;

  const eligible = (role: AccountRoleAssignment) => (account: Account) =>
    account.account_type === role.account_type &&
    account.is_active &&
    !account.is_reporting_account &&
    account.workflow_status === "approved";
  const hasEligible = (role: AccountRoleAssignment) => (accounts ?? []).some(eligible(role));

  const total = listing.roles.length;
  // Working means money can post: an approved account is behind the role, even
  // while a change to it is going through.
  const working = listing.roles.filter((role) => role.account).length;
  const percent = total ? Math.round((working / total) * 100) : 0;
  const counts = roleStageCounts(listing.roles);
  const term = search.trim().toLowerCase();
  const matches = (role: AccountRoleAssignment) =>
    (!stage || roleStage(role) === stage) &&
    (!term ||
      [role.label, role.help_text, role.group, role.account?.name ?? "", role.account?.code ?? "", role.pending_account?.name ?? ""]
        .some((value) => value.toLowerCase().includes(term)));
  const shown = listing.roles.filter(matches).length;

  const bulk = [
    canPropose && readyToSend > 0 && {
      text: `${readyToSend} ${readyToSend === 1 ? "change is" : "changes are"} ready to send for review.`,
      action: `Send ${readyToSend === 1 ? "it" : `all ${readyToSend}`} for review`,
      onClick: onSubmitAll,
    },
    canReview && readyToReview > 0 && {
      text: `${readyToReview} first-time ${readyToReview === 1 ? "assignment is" : "assignments are"} waiting for your review.`,
      action: `Review ${readyToReview === 1 ? "it" : `all ${readyToReview}`}`,
      onClick: onVerifyAll,
    },
    canApprove && readyToApprove > 0 && {
      text: `${readyToApprove} first-time ${readyToApprove === 1 ? "assignment is" : "assignments are"} reviewed and waiting for approval. A change to a role already in use is approved on its own.`,
      action: `Approve ${readyToApprove === 1 ? "it" : `all ${readyToApprove}`}`,
      onClick: onApproveAll,
    },
  ].filter(Boolean) as { text: string; action: string; onClick: () => void }[];

  return (
    <div className="space-y-4">
      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-start justify-between gap-6 px-6 py-5">
          <div className="max-w-2xl">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <GitBranch className="h-5 w-5" /> What each account is used for
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              The software writes entries for you, so it needs to know which account to use in each
              situation. Build the chart however you like — several roles may share one account.
            </p>
          </div>
          <div className="w-full max-w-xs">
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-bold tabular-nums text-gray-900">
                {working}
                <span className="text-base font-medium text-gray-400"> / {total}</span>
              </span>
              <span className="text-xs font-medium text-gray-500">roles working · {percent}%</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className={`h-full rounded-full transition-all ${percent === 100 ? "bg-green-500" : "bg-primary-600"}`}
                style={{ width: `${percent}%` }}
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
              <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-green-500" />{counts.in_use} in use</span>
              <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-blue-500" />{counts.in_progress} in progress</span>
              <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" />{counts.unassigned} not set up</span>
            </div>
          </div>
        </div>
        {listing.unassigned_count > 0 && (
          <div className="flex items-start gap-2 border-t border-amber-200 bg-amber-50 px-6 py-2.5 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              <strong>{listing.unassigned_count} still to set up.</strong> Anything that needs one of these refuses to
              post until it is assigned — set them up in any order; the parts that are ready already work.
            </span>
          </div>
        )}
        {bulk.map((item) => (
          <div key={item.action} className="flex flex-wrap items-center justify-between gap-3 border-t border-primary-100 bg-primary-50/60 px-6 py-2.5">
            <p className="text-sm text-primary-900">{item.text}</p>
            <button type="button" className={`${smallButton} bg-primary-600 text-white hover:bg-primary-700`} onClick={item.onClick}>
              {item.action} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-content space-y-4 pt-6">
          <div className="filter-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div role="group" aria-label="Setup stage" className="segmented">
                {STAGE_TABS.map(([value, label]) => (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={stage === value}
                    onClick={() => setStage(value)}
                    className="segmented-option"
                  >
                    {label}
                    <span className="ml-1.5 tabular-nums opacity-70">{counts[value]}</span>
                  </button>
                ))}
              </div>
              {(stage || term) && (
                <button
                  type="button"
                  onClick={() => { setStage(""); setSearch(""); }}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                >
                  <X className="h-3.5 w-3.5" /> Clear filters
                </button>
              )}
            </div>
            <label className="filter-field block">
              Search
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="input-compact pl-8"
                  placeholder="Role, what it is for, or the account behind it"
                />
              </div>
            </label>
          </div>

          {!shown && (
            <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
              No roles match these filters.
            </div>
          )}

          {listing.groups.map((group) => {
            const all = listing.roles.filter((role) => role.group === group);
            const roles = all.filter(matches);
            if (!roles.length) return null;
            const groupWorking = all.filter((role) => role.account).length;
            return (
              <section key={group} className="overflow-hidden rounded-lg border border-gray-200">
                <header className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">{group}</h3>
                  <span className="inline-flex items-center gap-2 text-[11px] text-gray-500">
                    <span className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200">
                      <span
                        className="block h-full rounded-full bg-green-500"
                        style={{ width: `${(groupWorking / all.length) * 100}%` }}
                      />
                    </span>
                    {groupWorking} of {all.length} working
                  </span>
                </header>
                <ul className="divide-y divide-gray-100">
                  {roles.map((role) => {
                    const status = STATUS[role.workflow_status] ?? { label: role.workflow_status, dot: "bg-gray-400" };
                    const pending = role.pending_account;
                    const canChoose = canPropose && ["unassigned", "draft", "returned", "approved", "rejected"].includes(role.workflow_status);
                    return (
                      <li
                        key={role.role}
                        className="grid gap-3 px-4 py-3 transition-colors hover:bg-gray-50/70 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_17rem] md:items-center"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-sm font-semibold text-gray-900">{role.label}</span>
                            <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium capitalize text-gray-600">
                              <span className={`h-1.5 w-1.5 rounded-full ${TYPE_DOTS[role.account_type]}`} />
                              {role.account_type}
                            </span>
                            {role.is_control && (
                              <span className="rounded bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">Control</span>
                            )}
                          </div>
                          <p className="mt-0.5 line-clamp-2 text-xs leading-5 text-gray-500" title={role.help_text}>
                            {role.help_text}
                          </p>
                        </div>

                        <div className="min-w-0 space-y-1 text-xs">
                          <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
                            <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} /> {status.label}
                          </span>
                          {role.account ? (
                            <p className="truncate text-gray-900">
                              <Check className="mr-1 inline h-3.5 w-3.5 text-green-600" />
                              <span className="font-mono">{role.account.code}</span> · {role.account.name}
                            </p>
                          ) : (
                            !pending && <p className="text-gray-400">No account assigned</p>
                          )}
                          {pending && (
                            <p className="truncate text-primary-800">
                              <ArrowRight className="mr-1 inline h-3.5 w-3.5" />
                              <span className="font-mono">{pending.code}</span> · {pending.name}
                              {role.workflow_status !== "approved" && <span className="text-primary-600"> (proposed)</span>}
                            </p>
                          )}
                          {role.workflow_status === "returned" && role.return_reason && (
                            <p className="text-orange-700">Sent back: {role.return_reason}</p>
                          )}
                        </div>

                        <div className="flex flex-col gap-1.5 md:items-end">
                          {canChoose && (hasEligible(role) ? (
                            <AccountSelect
                              className="w-full text-sm"
                              accounts={accounts}
                              isSelectable={eligible(role)}
                              placeholder={role.account || pending ? "Change to…" : "Choose an account…"}
                              value=""
                              onChange={(next) => {
                                if (next) onPropose({ role: role.role, account: Number(next) });
                              }}
                            />
                          ) : (
                            <p className="text-xs text-gray-500 md:text-right">
                              No approved {role.account_type} account yet.{" "}
                              {onCreateAccount && (
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-0.5 font-medium text-primary-700 hover:underline"
                                  onClick={() => onCreateAccount(role.account_type)}
                                >
                                  <Plus className="h-3 w-3" /> Create one
                                </button>
                              )}
                            </p>
                          ))}
                          <div className="flex flex-wrap gap-1.5 md:justify-end">
                            {canPropose && pending && ["draft", "returned"].includes(role.workflow_status) && (
                              <button type="button" className={`${smallButton} bg-primary-600 text-white hover:bg-primary-700`} onClick={() => onSubmit(role.role)}>
                                Send for review
                              </button>
                            )}
                            {canReview && role.workflow_status === "submitted" && (
                              <>
                                <button type="button" className={`${smallButton} bg-primary-600 text-white hover:bg-primary-700`} onClick={() => onVerify({ role: role.role })}>
                                  Reviewed
                                </button>
                                <button
                                  type="button"
                                  className={`${smallButton} text-amber-700 ring-1 ring-inset ring-amber-200 hover:bg-amber-50`}
                                  onClick={() => { setReturning({ role: role.role, label: role.label }); setReason(""); }}
                                >
                                  Send back
                                </button>
                              </>
                            )}
                            {canApprove && role.workflow_status === "verified" && (
                              <button type="button" className={`${smallButton} bg-green-600 text-white hover:bg-green-700`} onClick={() => onApprove(role.role)}>
                                Approve
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      </div>

      {returning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-lg">
            <h3 className="font-semibold text-gray-900">Send back “{returning.label}”</h3>
            <p className="mt-1 text-sm text-gray-500">Say what needs changing so it can be corrected.</p>
            <div className="mt-3">
              <AIAssistField
                rows={3}
                autoFocus
                value={reason}
                onChange={(next) => setReason(next)}
                placeholder="e.g. Point this at the Stanbic fees account, not petty cash."
                context={{
                  key: "financial.approval_note",
                  params: { document_type: "financial.accountrole", role: returning.role, purpose: "return" },
                }}
                draftLabel="Draft what to change"
                draftHint="Reads the proposed change. Jot rough notes first for a better draft."
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="px-3 py-1.5 text-sm text-gray-500" onClick={() => setReturning(null)}>Cancel</button>
              <button
                className="btn btn-primary px-3 py-1.5 text-sm"
                disabled={!reason.trim()}
                onClick={() => { onReturn({ role: returning.role, reason: reason.trim() }); setReturning(null); }}
              >
                Send back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
